import styled from '@emotion/styled';
import type { ObjectId } from '@racemap/sdk/schema/base';
import { capitalize, isNotEmptyString } from '@racemap/utilities/functions/utils';
import type { RacemapStarters } from '@racemap/utilities/types/events';
import { Alert, Select, type SelectProps, Space, Tag, Typography } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { IconExclamationTriangle } from '../BasicComponents/Icons';
import { PopoverHint } from '../BasicComponents/PopoverHint';

const { Text } = Typography;

interface Props {
  value: Immutable<Array<string>> | null;
  onChange: (newValue: Array<string>) => void;
  groups: Array<string>;
  eventId: string | ObjectId;
  hint?: string;
  title?: string;
}

export const TagGroupChooser: FC<Props> = ({ value, onChange, groups, eventId, title, hint }) => {
  const currentValue = value != null ? Array.from(value) : [];
  const abandonedTagGroups = value?.filter((tag) => !groups.includes(tag));
  const hasAbandonedTagGroups = abandonedTagGroups != null && abandonedTagGroups.length > 0;
  const options: SelectProps['options'] = [...groups, ...(abandonedTagGroups || [])].map((tag) => ({
    value: tag,
    label: capitalize(tag),
  }));

  if (groups.length === 0 && value?.length === 0) {
    return (
      <Alert
        showIcon
        description={
          <>
            Add tags for each participant such as age group, club, and nationality{' '}
            <Link to={`/admin/events/${eventId.toString()}/participants`}>here</Link>.
          </>
        }
      />
    );
  }

  return (
    <Container orientation="vertical">
      <Space size={3}>
        {isNotEmptyString(title) && <Text strong>{title}</Text>}
        {isNotEmptyString(hint) && <PopoverHint>{hint}</PopoverHint>}
      </Space>
      <Select
        allowClear
        value={currentValue}
        onChange={onChange}
        options={options}
        mode="multiple"
        placeholder="Choose tag groups"
        tagRender={({ value, ...props }) => (
          <TagRender value={value} {...props} abandoned={!!abandonedTagGroups?.includes(value)} />
        )}
        status={hasAbandonedTagGroups ? 'error' : undefined}
      />
    </Container>
  );
};

export const getTagCategoriesOfStarters = (
  starters?: RacemapStarters | Immutable<RacemapStarters>,
): Array<string> => {
  if (starters == null) return [];
  const startersArray = Array.from(starters.values());
  const tagsArrays = startersArray.map((item) =>
    item.tags != null ? Object.keys(item?.tags) : [],
  );
  const uniqueTags = [...new Set(tagsArrays.flat(1))];

  return uniqueTags;
};

const Container = styled(Space)`
  width: 100%;

  .ant-select {
    width: 100%;
  }
`;

interface TagRenderProps {
  label: React.ReactNode;
  value: string;
  closable: boolean;
  onClose: () => void;
  abandoned: boolean;
}

const TagRender: FC<TagRenderProps> = ({ value, closable, onClose, abandoned, label }) => {
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const finalLabel = abandoned ? (
    <Space size={4} title="No starter is in this tag group!" className="abandoned-tag-group">
      <IconExclamationTriangle />
      {label || value}
    </Space>
  ) : (
    label || value
  );

  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      color={abandoned ? 'red' : undefined}
      className="tag-group-chooser-tag"
    >
      {finalLabel}
    </Tag>
  );
};
